import { memo, useEffect, useMemo } from "react";
import { useTranslate } from "helpers";
import { validateYupWithFeedback } from "services/forms/validation/feedback";
import { useHandleTextFieldChange } from "services/forms/hooks";
import { TextField } from "ui/components/TextField/TextField";
import Toggle from "ui/components/Toggle/Toggle";
import Badge from "ui/components/Badge/Badge";
import { ENTITY_TYPE_LABELS } from "services/api/notifications/consts";
import {
  buildNotificationSchedule,
  formatScheduleDate,
  getExampleExpiryDate,
} from "ui/modules/notifications/schedule";
import { NOTIFICATION_STEP4_SCHEMA } from "../validation";
import { StepProps } from "../types";

const Step4Rules: React.FC<StepProps> = ({
  formValues,
  setFormValues,
  errors,
  setErrors,
  registerValidator,
}) => {
  const { t } = useTranslate();
  const handleTextChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleToggleChange = (_id: string, name: string, value: boolean) => {
    setFormValues((prev) => {
      if (name === "isRepeating" && value && !prev.repeatIntervalDays && !prev.repeatCount) {
        return {
          ...prev,
          [name]: value,
          repeatIntervalDays: 1,
          repeatCount: prev.daysBeforeExpiry ?? 1,
        };
      }
      return { ...prev, [name]: value };
    });
  };
  useEffect(() => {
    if (registerValidator) {
      registerValidator(() => validateYupWithFeedback(formValues, NOTIFICATION_STEP4_SCHEMA, setErrors));
    }
  }, [formValues]);

  return (
    <div className="flex justify-center h-full px-4 mt-[100px]">
      <div className="w-full max-w-4xl">
        <div className="flex flex-col gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              Pravila slanja
            </h1>
            <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
              Postavite kada i kako se notifikacija šalje
            </p>
          </div>

          <div className="border border-dashed bg-white border-blue-300">
            <div className="flex items-center justify-between border-b border-dashed border-blue-300 px-4 py-4">
              <div>
                <div className="text-md font-medium text-slate-900">Slanje notifikacije?</div>
                <p className="text-sm text-slate-500">Koliko dana prije isteka <strong>{formValues.entityType != null ? ENTITY_TYPE_LABELS[formValues.entityType]?.toLowerCase() : "entiteta"}</strong> šaljemo obavijest?</p>
              </div>
              <div className="w-48">
                <TextField
                  type="number"
                  name="daysBeforeExpiry"
                  value={formValues.daysBeforeExpiry ?? ""}
                  onChange={handleTextChange}
                  error={errors.daysBeforeExpiry}
                  required
                  width="full"
                  cssClassName="bg-white"
                  groupAppend="dana"
                />
              </div>
            </div>

            <div className="flex items-center justify-between border-b border-dashed border-blue-300 px-4 py-4">
              <div>
                <div className="text-md font-medium text-slate-900">Obavijest o nedostatku datuma na <strong>{formValues.entityType != null ? ENTITY_TYPE_LABELS[formValues.entityType]?.toLowerCase() : "entitetu"}</strong></div>
                <p className="text-sm text-slate-500">npr. obavijesti me ako na nekom od <strong>{formValues.entityType != null ? ENTITY_TYPE_LABELS[formValues.entityType]?.toLowerCase() : "entiteta"}</strong> nedostaje datum isteka</p>
              </div>
              <Toggle
                id="notifyIfDateMissing"
                name="notifyIfDateMissing"
                value={formValues.notifyIfDateMissing}
                onChange={handleToggleChange}
              />
            </div>

            <div className="px-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-md font-medium text-slate-900">Ponovi notifikaciju</div>
                  <p className="text-sm text-slate-500">Automatski ponavljaj slanje nakon prvog obavještenja</p>
                </div>
                <Toggle
                  id="isRepeating"
                  name="isRepeating"
                  value={formValues.isRepeating}
                  onChange={handleToggleChange}
                />
              </div>
              {formValues.isRepeating && (
                <div className="flex flex-col gap-3 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-700">{t("admin.notification.field.repeatIntervalDays")}</div>
                      <p className="text-sm text-slate-500">Koliko dana između svakog slanja</p>
                    </div>
                    <div className="w-48">
                      <TextField
                        type="number"
                        name="repeatIntervalDays"
                        value={formValues.repeatIntervalDays ?? ""}
                        onChange={handleTextChange}
                        error={errors.repeatIntervalDays}
                        width="full"
                        cssClassName="bg-white"
                        groupPrepend="svakih"
                        groupAppend="dana"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-700">{t("admin.notification.field.repeatCount")}</div>
                      <p className="text-sm text-slate-500">Ukupan broj ponavljanja</p>
                    </div>
                    <div className="w-48">
                      <TextField
                        type="number"
                        name="repeatCount"
                        value={formValues.repeatCount ?? ""}
                        onChange={handleTextChange}
                        error={errors.repeatCount}
                        width="full"
                        cssClassName="bg-white"
                         groupAppend="puta"
                      />
                    </div>
                  </div>

                  <SchedulePreview
                    daysBeforeExpiry={formValues.daysBeforeExpiry}
                    repeatIntervalDays={formValues.repeatIntervalDays}
                    repeatCount={formValues.repeatCount}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SchedulePreview: React.FC<{
  daysBeforeExpiry?: number | null;
  repeatIntervalDays?: number | null;
  repeatCount?: number | null;
}> = ({ daysBeforeExpiry, repeatIntervalDays, repeatCount }) => {
  // Stvarni istek se ne zna dok se ne veže entitet, pa se plan crta na izmišljenom
  // datumu za 30 dana — dovoljno da se vidi razmak između slanja.
  const expiryDate = useMemo(() => getExampleExpiryDate(), []);

  const schedule = useMemo(
    () => buildNotificationSchedule(expiryDate, daysBeforeExpiry, repeatIntervalDays, repeatCount),
    [expiryDate, daysBeforeExpiry, repeatIntervalDays, repeatCount]
  );

  if (schedule.length === 0) return null;

  return (
    <div className="mt-4 border border-dashed border-blue-300 rounded-sm p-3">
      <div className="text-xs font-medium text-slate-500 mb-2">
        Plan slanja (primjer za istek {formatScheduleDate(expiryDate)}):
      </div>
      <div className="flex flex-col gap-1.5">
        {schedule.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-slate-400 w-20 shrink-0">{formatScheduleDate(item.date)}</span>
            <Badge text={item.label} variant={item.variant} height="small" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(Step4Rules);
